import { Box, Stack } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { FieldValues, useFormContext } from "react-hook-form";

import FormStepperHeader from "./FormStepperHeader";
import FormStepperActions from "./FormStepperActions";
import { genericMemo } from "../../../../utils/utils";
import { IFormStepperProps } from "./models";
import NotAvaiableModal from "./NotAvaiableModal";
import { useTriageContext } from "../../../../store/contexts/TriageContext/TriageContext";

const FormStepper = <T extends FieldValues>({
  steps,
  finishLabel,
  onFinish,
  initialStep,
  canGoBack,
  error,
}: IFormStepperProps<T>) => {
  const { trigger } = useFormContext<T>();

  const [open, setOpen] = useState(false);

  const { activeStep, onActiveStep } = useTriageContext();

  const handleNext = useCallback(async () => {
    const valid = await trigger(steps[activeStep].fields);
    if (valid) {
      onActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }, [trigger, activeStep, steps, onActiveStep]);

  const handleBack = () => {
    onActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (error && error.errors.GetCalendarConsultationAvailable) {
      onActiveStep(3);
      setOpen(true);
    }
  }, [error, onActiveStep]);

  return (
    <Stack
      sx={{
        height: "100%",
        paddingInline: 1,
        gap: 2,
        width: "100%",
        overflow: "auto",
      }}
    >
      <NotAvaiableModal onClose={() => setOpen(false)} open={open} />
      <FormStepperHeader<T> activeStep={activeStep} steps={steps} />
      <Box width={"100%"} overflow={"auto"} sx={{ paddingInline: 2 }}>
        {steps[activeStep].element}
      </Box>
      <FormStepperActions
        finishLabel={finishLabel}
        activeStep={activeStep}
        canGoBack={canGoBack}
        isLastStep={activeStep === steps.length - 1}
        onBack={handleBack}
        onNext={handleNext}
        onFinish={onFinish}
      />
    </Stack>
  );
};

export default genericMemo(FormStepper);
